import { mmFetchWithResponse } from './mmFetchWithResponse';

const customerApi = {
    addCredit: async (subID, sendParams) => {
        const url = `api/credits/v1/${subID}/create`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    applyCredit: async (subID, sendParams) => {
        const url = `api/discounts/v1/${subID}/auto`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    applyStoreCredit: async (chargeID, sendParams) => {
        const url = `api/charge/v3/${chargeID}/credit`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    cancelOrderAuth: async (chargeID, sendParams) => {
        const url = `api/charge/v3/${chargeID}/cancelAuth`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    cancelOrderLineItem: async (chargeID, sendParams) => {
        const url = `api/charge/v3/${chargeID}/refund/cancel`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    cancelAndRefundByShipment: async (chargeID, shipmentID, sendParams) => {
        const url = `api/charge/v3/${chargeID}/refund/cancel/shipment/${shipmentID}`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    cancelShipment: async (chargeID, shipmentID) => {
        const url = `api/charge/v3/${chargeID}/cancel/${shipmentID}`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
        });
        return response;
    },
    cancelSubscription: async (subID) => {
        const url = `api/subscription/v1/${subID}/cancel`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
        });
        return response;
    },
    /**
     * Update delivery day and/or order interval for a user.
     * @param {string} subscriptionID - Subscription ID of the user.
     * @param {Object} sendParams - The params to send to the server.
     * @param {string} sendParams.prefDeliveryDay - New preferred delivery day.
     * @param {string} sendParams.orderInterval - [Optional] New preferred order unit interval (1 = weekly, 3 = biweekly even weeks, 4 = biweekly odd weeks).
     */
    changeDeliveryInformation: async (subID, sendParams) => {
        const url = `api/subscription/v1/${subID}`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    convertToAutoship: async (subID) => {
        const url = `api/subscription/v1/${subID}/autoship`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
        });
        return response;
    },
    convertToMembership: async (subID, reason, secondaryReason) => {
        const url = `api/subscription/v1/${subID}/membership`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify({ reason, secondaryReason }),
        });
        return response;
    },
    getAccountInfo: async (custID) => {
        const url = `api/customer/v2/account/${custID}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getChangelog: async (subID) => {
        const url = `api/subscription/v1/${subID}/changelog`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getChangelogHistorical: async (subID) => {
        const url = `api/subscription/v1/${subID}/changelog/historical`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getBulkCustomerUploads: async () => {
        const url = `api/customer/v1/bulkCustomerUploads`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getDiscountDetails: async (subID) => {
        const url = `api/discounts/v1/${subID}/list`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getEmailNotificationPreferences: async (custID) => {
        const url = `api/customer/v1/${custID}/notifications`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getCustomerCommunications: async (custID) => {
        const url = `api/customer/v1/${custID}/communications`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getOrderDetails: async (orderNum) => {
        const url = `api/order/v4/${orderNum}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getOrders: async (custID) => {
        const url = `api/customer/v3/${custID}/orders?page=0&pageSize=40`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getRefundableItems: async (chargeID) => {
        const url = `api/charge/v3/${chargeID}/refundableItems`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getSubscription: async (custID) => {
        const url = `api/subscription/v2/${custID}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getDrinksOrders: async (custID) => {
        const url = `api/partnerships/v1/drinksOrders/${custID}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getDrinksOrderDetails: async (drinksOrderID) => {
        const url = `api/partnerships/v1/drinksOrders/details/${drinksOrderID}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getDrinksRefundableItems: async (drinksOrderID) => {
        const url = `api/partnerships/v1/drinksRefund/${drinksOrderID}/refundableDrinksItems`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getV2Plans: async () => {
        const url = `api/marketplace/v1/autoship/plans`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getPersonalizationInfo: async (customerID) => {
        const url = `api/customer/v1/personalization-info/${customerID}`;
        return mmFetchWithResponse(url);
    },
    overrideSeedingRange: async (customerID, min, max) => {
        const url = `api/customer/v1/personalization-info/default/${customerID}`;
        return mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify({ min, max }),
        });
    },
    inspectCharge: async (chargeID) => {
        const url = `api/charge/v1/${chargeID}/inspect`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    massCancelOrders: async (orderFile) => {
        const formData = new FormData();
        formData.append('orderIDFile', orderFile);
        const url = `api/customer/v1/mass-cancel`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
        return response;
    },
    massFullRefund: async (refundFile, reason) => {
        const formData = new FormData();
        formData.append('refundIDFile', refundFile);
        formData.append('reason', reason);
        const url = `api/charge/v3/massRefund/full`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
        return response;
    },
    pauseSubscription: async (subID, numMonths) => {
        const url = `api/subscription/v1/${subID}/pause`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify({ numMonths }),
        });
        return response;
    },
    reactivateSubscription: async (subID) => {
        const url = `api/subscription/v1/${subID}/reactivate`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
        });
        return response;
    },
    refundItems: async (refundChargeID, sendParams) => {
        const url = `api/charge/v2/${refundChargeID}/refund/items`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    refundLineItems: async (refundChargeID, sendParams) => {
        const url = `api/charge/v3/${refundChargeID}/refund/items`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    refundOrder: async (refundChargeID, sendParams) => {
        const url = `api/charge/v2/${refundChargeID}/refund`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    refundOrderLineItem: async (chargeID, sendParams) => {
        const url = `api/charge/v3/${chargeID}/refund/full`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    refundByShipment: async (chargeID, sendParams) => {
        const url = `api/charge/v3/${chargeID}/refund/shipment`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    refundShipping: async (chargeID, sendParams) => {
        const url = `api/charge/v3/${chargeID}/refund/shipping`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    refundColdKeeper: async (chargeID, sendParams) => {
        const url = `api/charge/v3/${chargeID}/refund/coldKeeperFee`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    refundFrozenFee: async (chargeID, sendParams) => {
        const url = `api/charge/v3/${chargeID}/refund/frozenFee`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    refundTip: async (refundChargeID) => {
        const url = `api/charge/v3/${refundChargeID}/refund/tip`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
        });
        return response;
    },
    fullRefundDrinksOrder: async (drinksOrderID, sendParams) => {
        const url = `api/partnerships/v1/drinksRefund/${drinksOrderID}/full`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    partialRefundDrinksOrder: async (drinksOrderID, sendParams) => {
        const url = `api/partnerships/v1/drinksRefund/${drinksOrderID}/partial`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    removeBilling: async (subID) => {
        const url = `api/subscription/v4/${subID}/billing/remove`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
        });
        return response;
    },
    removeEBTCard: async (subID) => {
        const url = `api/subscription/v4/${subID}/billing/ebt/remove`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
        });
        return response;
    },
    removeDiscount: async (discountID) => {
        const url = `api/discounts/v1/${discountID}/remove`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
        });
        return response;
    },
    checkDiscountCode: async (customerID, code) => {
        const url = `api/discounts/v1/validate/check?code=${encodeURIComponent(code)}${customerID ? `&custID=${customerID}` : ''}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    claimDiscountCode: async (subID, code) => {
        const url = `api/discounts/v1/${subID}?code=${encodeURIComponent(code)}`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
        });
        return response;
    },
    resetPassword: async (custID) => {
        const url = `api/customer/v2/account/${custID}/resetPassword`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    resumeSubscription: async (subID) => {
        const url = `api/subscription/v1/${subID}/resume`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
        });
        return response;
    },
    searchCustomers: async (searchTerm, searchBy) => {
        const url = `api/customer/v1/search?${searchBy}=${searchTerm.trim()}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    searchCarriers: async (zip) => {
        const url = `api/customer/v1/carriers/${zip}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    skipCharge: async (chargeID) => {
        const url = `api/charge/v2/${chargeID}/skip`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify({ reason: 'Customer Care Skip' }),
        });
        return response;
    },
    unskipCharge: async (chargeID) => {
        const url = `api/charge/v2/${chargeID}/unskip`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
        });
        return response;
    },
    updateEmail: async (subID, email) => {
        const url = `api/subscription/v2/${subID}/email`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify({ email }),
        });
        return response;
    },
    updateEmailNotificationPreferences: async (custID, sendParams) => {
        const url = `api/customer/v1/${custID}/notifications`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    updateShippingAddress: async (subID, address) => {
        const url = `api/subscription/v1/${subID}/address`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(address),
        });
        return response;
    },
    updateSMSNotificationPreferences: async (custID, sendParams) => {
        const url = `api/customer/v1/${custID}/sms`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    updateSubscription: async (subID, sendParams) => {
        /**
            valid send param keys: productID, orderInterval, numItems, prefDeliveryDay
         */
        const url = `api/subscription/v1/${subID}`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    updatePreferredTip: async (subID, newTip) => {
        const sendParams = { tipAmount: newTip };
        const url = `api/subscription/v1/${subID}/tips/preferences`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    getCreditsBalance: async (subID) => {
        const url = `api/credits/v1/${subID}/balance`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    removeCredit: async (creditID) => {
        const url = `api/credits/v1/${creditID}/remove`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
        });
        return response;
    },
    getGiftCodes: async (subID) => {
        const url = `api/giftCodes/v1/${subID}/list`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getMembershipSummary: async (custID) => {
        const url = `api/plus/v1/${custID}/summary`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    updateMembershipAutoRenew: async (custID, isAutoRenew) => {
        const formData = new FormData();
        formData.append('autoRenewalFlag', isAutoRenew);

        const url = `api/plus/v1/${custID}/autorenew`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
        return response;
    },
    cancelMembership: async (custID) => {
        const formData = new FormData();
        formData.append('reason', 'CS Cancelled Membership');

        const url = `api/plus/v1/${custID}/cancelMembership`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
        return response;
    },
    cancelTrial: async (custID) => {
        const formData = new FormData();
        formData.append('reason', 'CS Cancelled Free Trial');

        const url = `api/plus/v1/${custID}/cancelTrial`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
        return response;
    },
    refundMembership: async (custID, plusMembershipChargeID, refundType, refundReason, refundReasonSecondary) => {
        const formData = new FormData();
        formData.append('plusMembershipChargeID', plusMembershipChargeID);
        formData.append('refundReason', refundReason);
        formData.append('refundReasonSecondary', refundReasonSecondary);
        formData.append('refundType', refundType);

        const url = `api/plus/v1/${custID}/refund`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
        return response;
    },
    grantFreeTrial: async (custID, endDate) => {
        const formData = new FormData();
        formData.append('endDate', endDate);

        const url = `api/plus/v1/${custID}/grantFreeTrial`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
        return response;
    },
    getAllPromoCodes: async (subID) => {
        const url = `api/customer/v1/promoCodeList/${subID}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    resendGiftCode: async (code) => {
        const url = `api/giftCodes/v1/resend?code=${code}`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
        });
        return response;
    },
    getDelayBanners: async () => {
        const url = `api/customer/v1/serviceDelayBanners`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    updateDelayBanners: async (sendParams) => {
        const { id } = sendParams;
        const url = `api/customer/v1/serviceDelayBanner/${id}`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    createDelayBanner: async (sendParams) => {
        const formData = new FormData();
        Object.keys(sendParams).forEach((key) => {
            formData.append(key, sendParams[key]);
        });

        const url = `api/customer/v1/serviceDelayBanner`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
        return response;
    },
    getCognitoStatus: async (custID) => {
        const url = `api/customer/v1/${custID}/cognito/exists`;
        const response = await mmFetchWithResponse(url);
        return response;
    },

    createCognitoAccount: async (custID, sendParams) => {
        const url = `api/customer/v1/${custID}/cognito/create`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    createCognitoTempPassword: async (custID, sendParams = {}) => {
        const url = `api/customer/v1/${custID}/cognito/settemp`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    clearBrazeSpamFlag: async (custID, sendParams = {}) => {
        const url = `api/customer/v2/clearBrazeSpamFlag/${custID}`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
};

export { customerApi };
